import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { SidebarContainer } from 'src/components/sidebar/container';
import { SidebarHeader } from 'src/components/sidebar/header';
import { SidebarLinks } from 'src/components/sidebar/links';
import {
  endNavCollapse,
  NAV_COLLAPSE_ABORT_MS,
  NAV_FADE_IN_MS,
  NAV_FADE_OUT_MS,
  useNavCollapsing,
} from 'src/components/sidebar/nav-chrome-transition';
import type { SectionLink } from 'src/models/section-links';

type Props = {
  imageSrc?: string;
  links: SectionLink[];
  title: string;
};

export default function AdminSidebar(props: Props): JSX.Element {
  const { imageSrc, links, title } = props;
  const collapsing = useNavCollapsing();

  // Was the nav mid-collapse at mount (i.e. arriving back from the editor route)? Captured
  // once so later updates to `collapsing` don't retroactively change how we started.
  const wasCollapsingAtMount = useRef(collapsing).current;
  const [visible, setVisible] = useState(!wasCollapsingAtMount);

  // Deliberately one effect, not split into mount + [collapsing]: both would fire in the same
  // commit on mount, and the second would immediately re-hide what the first just made visible.
  // Guarding the first invocation as "mount" sidesteps that.
  const hasHandledInitial = useRef(false);

  useEffect(() => {
    if (!hasHandledInitial.current) {
      hasHandledInitial.current = true;
      if (wasCollapsingAtMount) {
        setVisible(true);
      }
      // Clears a stale armed flag if navigation into the editor was aborted after arming.
      endNavCollapse();
      return undefined;
    }

    if (collapsing) {
      setVisible(false);

      // Safety net: if the expected route swap never unmounts this component (guard no-op,
      // pushed URL === current), still being mounted when the timer fires means navigation
      // didn't happen -- self-heal instead of staying blank forever.
      const abortTimer = setTimeout(() => {
        endNavCollapse();
        setVisible(true);
      }, NAV_COLLAPSE_ABORT_MS);

      return () => clearTimeout(abortTimer);
    }

    return undefined;
  }, [collapsing, wasCollapsingAtMount]);

  return (
    <SidebarContainer>
      <SidebarContent $visible={visible}>
        <SidebarHeader imageSrc={imageSrc} title={collapsing ? null : title} />
        <SidebarLinks links={links} />
      </SidebarContent>
    </SidebarContainer>
  );
}

const SidebarContent = styled.div<{ $visible: boolean }>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${({ $visible }) => ($visible ? NAV_FADE_IN_MS : NAV_FADE_OUT_MS)}ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
